import { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import type { RecordingScheduleDayKey, RecordingScheduleSlots } from '@api/types';
import {
  RECORDING_SCHEDULE_DAY_KEYS,
  RECORDING_SCHEDULE_DAY_LABELS,
  SLOTS_PER_DAY,
  cloneRecordingScheduleSlots,
  formatSlotRowLabel,
} from '@lib/recording-schedule';

const SLOT_INDICES = Array.from({ length: SLOTS_PER_DAY }, (_, i) => i);

interface Props {
  value: RecordingScheduleSlots;
  onChange: (next: RecordingScheduleSlots) => void;
}

export function RecordingScheduleGrid({ value, onChange }: Props): JSX.Element {
  const paintRef = useRef<'select' | 'erase' | null>(null);

  useEffect(() => {
    const endPaint = (): void => {
      paintRef.current = null;
    };
    window.addEventListener('pointerup', endPaint);
    window.addEventListener('pointercancel', endPaint);
    return () => {
      window.removeEventListener('pointerup', endPaint);
      window.removeEventListener('pointercancel', endPaint);
    };
  }, []);

  const setSlot = useCallback(
    (day: RecordingScheduleDayKey, slotIndex: number, selected: boolean): void => {
      if (value[day][slotIndex] === selected) return;
      const next = cloneRecordingScheduleSlots(value);
      next[day][slotIndex] = selected;
      onChange(next);
    },
    [onChange, value],
  );

  const setAll = useCallback(
    (selected: boolean): void => {
      const next = cloneRecordingScheduleSlots(value);
      for (const d of RECORDING_SCHEDULE_DAY_KEYS) {
        for (let i = 0; i < SLOTS_PER_DAY; i += 1) {
          next[d][i] = selected;
        }
      }
      onChange(next);
    },
    [onChange, value],
  );

  const setDayAll = useCallback(
    (day: RecordingScheduleDayKey, selected: boolean): void => {
      const next = cloneRecordingScheduleSlots(value);
      for (let i = 0; i < SLOTS_PER_DAY; i += 1) {
        next[day][i] = selected;
      }
      onChange(next);
    },
    [onChange, value],
  );

  const toggleAllDay = useCallback(
    (day: RecordingScheduleDayKey): void => {
      const anyOff = value[day].some((v) => !v);
      setDayAll(day, anyOff);
    },
    [setDayAll, value],
  );

  const onCellPointerDown = (
    day: RecordingScheduleDayKey,
    slotIndex: number,
    e: React.PointerEvent,
  ): void => {
    if (e.button !== 0) return;
    e.preventDefault();
    const nextSelected = !value[day][slotIndex];
    paintRef.current = nextSelected ? 'select' : 'erase';
    setSlot(day, slotIndex, nextSelected);
  };

  const onCellPointerEnter = (day: RecordingScheduleDayKey, slotIndex: number, e: React.PointerEvent): void => {
    if (paintRef.current === null) return;
    if ((e.buttons & 1) === 0) return;
    const wantSelect = paintRef.current === 'select';
    setSlot(day, slotIndex, wantSelect);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          className={clsx(
            'rounded-md border border-border bg-surface-2 px-2.5 py-1 text-xs font-medium text-foreground',
            'hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          )}
          onClick={() => setAll(true)}
        >
          Select all
        </button>
        <button
          type="button"
          className={clsx(
            'rounded-md border border-border bg-surface-2 px-2.5 py-1 text-xs font-medium text-foreground',
            'hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          )}
          onClick={() => setAll(false)}
        >
          Clear all
        </button>
      </div>

      <div className="overflow-auto rounded-md border border-border bg-surface max-h-[min(70vh,520px)]">
        <table
          className="w-full min-w-[640px] border-collapse text-[11px]"
          role="grid"
          aria-label="Weekly recording schedule in 30-minute slots"
        >
          <thead>
            <tr>
              <th
                scope="col"
                className="sticky left-0 z-20 w-14 border border-border bg-surface px-1 py-2 text-left font-medium text-muted"
              >
                Time
              </th>
              {RECORDING_SCHEDULE_DAY_KEYS.map((day) => (
                <th
                  key={day}
                  scope="col"
                  className="border border-border bg-surface-2 px-0.5 py-1 text-center align-bottom font-medium text-foreground"
                >
                  <div className="text-xs">{RECORDING_SCHEDULE_DAY_LABELS[day]}</div>
                  <button
                    type="button"
                    className={clsx(
                      'mt-1 w-full rounded px-1 py-0.5 text-[10px] font-normal text-muted',
                      'hover:bg-surface hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
                    )}
                    onClick={() => toggleAllDay(day)}
                  >
                    All day
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SLOT_INDICES.map((slotIndex) => (
              <tr key={slotIndex} className="h-5">
                <th
                  scope="row"
                  className={clsx(
                    'sticky left-0 z-10 whitespace-nowrap border border-border bg-surface px-1 py-0 text-left font-normal text-muted',
                    slotIndex % 2 === 0 ? 'text-foreground' : 'text-muted',
                  )}
                >
                  {formatSlotRowLabel(slotIndex)}
                </th>
                {RECORDING_SCHEDULE_DAY_KEYS.map((day) => {
                  const selected = value[day][slotIndex];
                  return (
                    <td key={`${day}-${slotIndex}`} className="border border-border p-0" role="gridcell">
                      <button
                        type="button"
                        aria-selected={selected}
                        tabIndex={-1}
                        className={clsx(
                          'h-5 w-full min-w-[28px] border-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset',
                          selected
                            ? 'bg-accent/30 hover:bg-accent/40'
                            : 'bg-surface-2 hover:bg-surface',
                        )}
                        onPointerDown={(e) => onCellPointerDown(day, slotIndex, e)}
                        onPointerEnter={(e) => onCellPointerEnter(day, slotIndex, e)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted">
        Selected slots (accent tint) are recorded when the camera is in scheduled mode, active, and
        recording is enabled. Times follow the server{"'"}s effective timezone (saved per camera or
        <span className="font-mono"> DEFAULT_RECORDING_SCHEDULE_TIMEZONE</span>, default{' '}
        <span className="font-mono">Asia/Kolkata</span>). An empty grid means no scheduled windows.
      </p>
    </div>
  );
}
